import React, { useEffect, useRef, useState } from "react";

/**
 * CountUp — animation de compteur premium, discret, une seule fois.
 * Détecte automatiquement les chiffres dans la chaîne (ex: "24/7", "100%", "6")
 * et anime uniquement la partie numérique. Le reste (%, /7, …) est préservé.
 * Durée par défaut : 1400 ms, easing easeOutCubic.
 */
export default function CountUp({ value, duration = 1400, className = "" }) {
  const ref = useRef(null);
  const startedRef = useRef(false);
  const raf = useRef(null);

  // Extrait le premier bloc numérique (int ou float) et sépare préfixe/suffixe
  const str = String(value);
  const match = str.match(/([0-9]+([.,][0-9]+)?)/);
  const numStr = match ? match[1].replace(",", ".") : null;
  const target = numStr ? parseFloat(numStr) : null;
  const prefix = match ? str.slice(0, match.index) : str;
  const suffix = match ? str.slice(match.index + match[1].length) : "";
  const decimals = numStr && numStr.includes(".") ? numStr.split(".")[1].length : 0;

  const [display, setDisplay] = useState(target !== null ? 0 : str);

  useEffect(() => {
    if (target === null) return;
    const el = ref.current;
    if (!el) return;

    const runAnim = () => {
      if (startedRef.current) return;
      startedRef.current = true;
      const t0 = performance.now();
      const step = (t) => {
        const p = Math.min(1, (t - t0) / duration);
        // easeOutCubic
        const eased = 1 - Math.pow(1 - p, 3);
        setDisplay(target * eased);
        if (p < 1) raf.current = requestAnimationFrame(step);
        else setDisplay(target);
      };
      raf.current = requestAnimationFrame(step);
    };

    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            runAnim();
            io.disconnect();
          }
        });
      },
      { threshold: 0.1, rootMargin: "0px 0px -10% 0px" }
    );
    io.observe(el);

    // Fallback : si l'élément est déjà (au moins partiellement) visible au mount,
    // on lance l'animation immédiatement (certains navigateurs / cas de re-mount
    // ne déclenchent pas le callback initial de manière fiable).
    const rect = el.getBoundingClientRect();
    if (rect.top < window.innerHeight && rect.bottom > 0) {
      runAnim();
    }

    return () => {
      io.disconnect();
      if (raf.current) cancelAnimationFrame(raf.current);
    };
  }, [target, duration]);

  if (target === null) {
    return <span ref={ref} className={`nx-countup ${className}`}>{str}</span>;
  }

  const shown =
    typeof display === "number"
      ? decimals > 0
        ? display.toFixed(decimals)
        : Math.round(display).toString()
      : display;

  return (
    <span ref={ref} className={`nx-countup ${className}`}>
      {prefix}
      {shown}
      {suffix}
    </span>
  );
}
